// Microsoft Clarity service.
//
// The project ID is provided at container start by
// docker-image-req/inject-env.sh as window.config.CLARITY_PROJECT_ID
// (separate IDs for production and dev). For local development
// (`yarn start`, no runtime injection) it falls back to
// REACT_APP_CLARITY_PROJECT_ID from .env. If neither is set, every
// function here is a safe no-op, so Clarity can never break the app.

import { getEnvValue } from "./getEnvValue";

const PROJECT_ID = getEnvValue("CLARITY_PROJECT_ID", "");

let initialized = false;

export const isClarityEnabled = () => Boolean(PROJECT_ID);

/**
 * Load the Microsoft Clarity tag and start the queue. Call once at app
 * startup (done by <MicrosoftClarity />). Safe to call multiple times.
 *
 * This mirrors Clarity's official bootstrap snippet: it defines the
 * global `clarity` command queue, then async-loads the tag script for
 * the configured project ID.
 */
export function initClarity() {
  if (initialized || !isClarityEnabled()) return;
  initialized = true;

  (function (c, l, a, r, i) {
    c[a] =
      c[a] ||
      function () {
        (c[a].q = c[a].q || []).push(arguments);
      };
    const t = l.createElement(r);
    t.async = 1;
    t.src = "https://www.clarity.ms/tag/" + i;
    const y = l.getElementsByTagName(r)[0];
    y.parentNode.insertBefore(t, y);
  })(window, document, "clarity", "script", PROJECT_ID);
}

/**
 * Attach the signed-in user to the Clarity session so recordings and
 * heatmaps can be filtered by user. All subsequent activity in the
 * session is associated with this user.
 */
export function setClarityUser({ userId, email, name } = {}) {
  if (!isClarityEnabled() || !window.clarity || !email) return;
  // identify(customId, customSessionId, customPageId, friendlyName)
  window.clarity("identify", email, undefined, undefined, name || undefined);
  if (userId) window.clarity("set", "user_id", String(userId));
  window.clarity("set", "user_email", email);
  if (name) window.clarity("set", "user_name", name);
}

/**
 * Track a custom Clarity event from anywhere in the app:
 *   import { trackClarityEvent } from "../services/clarity";
 *   trackClarityEvent("message_sent");
 */
export function trackClarityEvent(eventName) {
  if (!isClarityEnabled() || !window.clarity) return;
  window.clarity("event", eventName);
}
